import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaWhatsapp } from 'react-icons/fa';
import { contactAPI } from '../services/api';
import { toast } from 'react-toastify';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = 'Contact Us | Kariyapperuma Car Sales';
    return () => { document.title = 'Kariyapperuma Car Sales'; };
  }, []);

  // Validation function
  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ''
          : 'Invalid email address';
      case 'phone':
        return /^[\d\s\-+()]{9,}$/.test(value)
          ? ''
          : 'Invalid phone number';
      case 'name':
        return value.trim().length >= 2 ? '' : 'Name must be at least 2 characters';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time validation if field has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submit
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'message' && key !== 'interest') { // message and interest are optional
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await contactAPI.submit(formData);
      if (response.data.success) {
        setSuccess(true);
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', interest: '', message: '' });
        setErrors({});
        setTouched({});

        // Reset success state after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="page-header">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="page-title">Get In Touch</h1>
            <p className="page-subtitle">Visit our showroom in Kadawatha or reach out to us</p>
          </motion.div>
        </div>
      </section>

      <section className="contact-section section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-card">
              <h2>Contact Information</h2>
              <div className="contact-items">
                <div className="contact-item">
                  <FaMapMarkerAlt className="contact-icon" />
                  <div>
                    <h4>Address</h4>
                    <p>600/4A, Kandy Road, Eldeniya,<br />Kadawatha <br></br> </p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=2X58%2BF3+Kadawatha"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontSize: '0.9rem', marginTop: '0.5rem', display: 'inline-block' }}
                    >
                      Open in Google Maps →
                    </a>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <div>
                    <h4>Phone</h4>
                    <p>+94 70 735 2969</p>
                    <small style={{ color: '#999' }}>Call for inquiries & appointments</small>
                  </div>
                </div>
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <div>
                    <h4>Email</h4>
                    <p>Kariyapperumacars@gmail.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <FaWhatsapp className="contact-icon" />
                  <div>
                    <h4>WhatsApp</h4>
                    <p>+94 70 735 2969</p>
                    <a
                      href="https://wa.me/94707352969"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontSize: '0.9rem', marginTop: '0.5rem', display: 'inline-block' }}
                    >
                      Chat on WhatsApp →
                    </a>
                  </div>
                </div>
                <div className="contact-item">
                  <FaClock className="contact-icon" />
                  <div>
                    <h4>Business Hours</h4>
                    <p>Monday - Saturday: 6:00 AM - 9:00 PM</p>
                    <p>Sunday: By Appointment</p>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="map-container">
                <iframe
                  src="https://maps.google.com/maps?q=2X58%2BF3+Kadawatha&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="350"
                  style={{ border: 0, borderRadius: '15px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Kariyapperuma Enterprises - 11/5, Kandy Road, Eldeniya, Kadawatha 11850"
                ></iframe>
                <div style={{
                  marginTop: '1rem',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  <p>📍 Located on Kandy Road, Kadawatha</p>
                </div>
              </div>
            </div>

            <div className="contact-form-card">
              <h2>Send Us a Message</h2>
              {success && (
                <motion.div
                  className="form-success"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  ✓ Message sent successfully! We'll get back to you soon.
                </motion.div>
              )}
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="form-group">
                  <label>
                    Full Name
                    <span className="required">*</span>
                  </label>
                  <motion.input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-input ${touched.name && errors.name ? 'is-invalid' : ''}`}
                    placeholder="Enter your full name"
                    autoComplete="off"
                  />
                  {touched.name && errors.name && (
                    <motion.span
                      className="form-error"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.name}
                    </motion.span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Phone Number
                    <span className="required">*</span>
                  </label>
                  <motion.input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-input ${touched.phone && errors.phone ? 'is-invalid' : ''}`}
                    placeholder="+94 71 234 5678"
                    autoComplete="off"
                  />
                  {touched.phone && errors.phone && (
                    <motion.span
                      className="form-error"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.phone}
                    </motion.span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Email Address
                    <span className="required">*</span>
                  </label>
                  <motion.input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-input ${touched.email && errors.email ? 'is-invalid' : ''}`}
                    placeholder="you@example.com"
                    autoComplete="off"
                  />
                  {touched.email && errors.email && (
                    <motion.span
                      className="form-error"
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.email}
                    </motion.span>
                  )}
                </div>
                <div className="form-group">
                  <label>Interested In</label>
                  <select 
                    name="interest" 
                    value={formData.interest} 
                    onChange={handleChange} 
                    className="form-select"
                  >
                    <option value="">Select a category</option>
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="hatchback">Hatchback</option>
                    <option value="van">Van / MPV</option>
                    <option value="other">Other / Not Sure</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Your Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="form-textarea"
                    placeholder="Tell us about your requirements, budget, or any questions..."
                    autoComplete="off"
                  ></textarea>
                </div>
                <motion.button
                  type="submit"
                  className="btn btn-primary btn-block btn-lg"
                  disabled={loading}
                  whileHover={!loading ? { scale: 1.02 } : {}}
                  whileTap={!loading ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="spinner-icon"
                      >
                        ⟳
                      </motion.span>
                      {' '}Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane /> Send Message
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
